import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Check, Loader2, ClipboardList } from "lucide-react";
import axios from "axios";
import WhatsAppIcon from "@/components/WhatsAppIcon";
import { buildWhatsAppLink } from "@/components/FloatingWhatsAppButton";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* ------------------------------------------------------------------ */
/*  16 SECTIONS  — every field maps 1:1 to the backend Pydantic model  */
/* ------------------------------------------------------------------ */
const SECTIONS = [
  {
    title: "Personal & Contact Information",
    fields: [
      { name: "full_name", label: "Full name *", required: true, placeholder: "First Last" },
      { name: "email", label: "Email *", required: true, type: "email", placeholder: "you@example.com" },
      { name: "phone", label: "Phone number *", required: true, placeholder: "+1 555 000 0000" },
      { name: "date_of_birth", label: "Date of birth", type: "date" },
      { name: "location", label: "Location (City / Country)", placeholder: "e.g. Los Angeles, USA" },
    ],
  },
  {
    title: "Body & Current Condition",
    fields: [
      { name: "height", label: "Height", placeholder: "e.g. 5'10\" / 178cm" },
      { name: "current_weight", label: "Current weight", placeholder: "e.g. 185 lbs / 84kg" },
      { name: "body_fat_estimate", label: "Body composition / body-fat estimate", placeholder: "e.g. ~12%" },
      { name: "current_physical_condition", label: "Current physical condition", area: true, placeholder: "Describe your current shape and how you feel about it." },
    ],
  },
  {
    title: "Goals",
    fields: [
      { name: "primary_goal", label: "Primary goal", area: true, placeholder: "e.g. Build muscle, lean out, compete." },
      { name: "specific_physique_goals", label: "Specific physique goals", area: true, placeholder: "Bodyparts to improve, symmetry, etc." },
      { name: "target_weight_composition", label: "Target weight / body composition" },
      { name: "competition_goals", label: "Competition goals (if any)", area: true, placeholder: "Federation, category, show, etc." },
      { name: "deadline_target_date", label: "Deadline or target date", type: "date" },
      { name: "coaching_outcomes", label: "What do you want to accomplish through coaching?", area: true },
    ],
  },
  {
    title: "Training History",
    fields: [
      { name: "current_training_program", label: "Current training program", area: true },
      { name: "training_experience", label: "Training experience (years)" },
      { name: "training_days_per_week", label: "Training days per week" },
      { name: "current_cardio", label: "Current cardio" },
      { name: "gym_availability", label: "Gym / training availability", area: true },
      { name: "favorite_exercises", label: "Favorite exercises" },
      { name: "least_favorite_exercises", label: "Least-favorite exercises" },
      { name: "strengths_weaknesses", label: "Current strengths and weaknesses", area: true },
    ],
  },
  {
    title: "Nutrition",
    fields: [
      { name: "current_diet", label: "Current diet", area: true },
      { name: "typical_daily_meals", label: "Typical daily meals", area: true, placeholder: "Walk us through a normal day of eating." },
      { name: "food_preferences", label: "Food preferences" },
      { name: "foods_avoided", label: "Foods you avoid" },
      { name: "allergies_intolerances", label: "Allergies / intolerances" },
      { name: "previous_diets", label: "Previous diets", area: true },
      { name: "current_calorie_macros", label: "Current calorie/macros (if known)", placeholder: "e.g. 2800 kcal / 220P / 300C / 70F" },
      { name: "eating_schedule", label: "Eating schedule" },
      { name: "digestion_concerns", label: "Digestion / GI concerns", area: true },
    ],
  },
  {
    title: "Health & Medical Background",
    fields: [
      { name: "medical_history", label: "Relevant medical history", area: true },
      { name: "current_medications", label: "Current medications" },
      { name: "recent_bloodwork", label: "Recent bloodwork / labs", area: true },
      { name: "previous_current_injuries", label: "Previous or current injuries", area: true },
      { name: "surgeries", label: "Surgeries", area: true },
      { name: "health_considerations", label: "Any health considerations the coach should know about", area: true },
    ],
  },
  {
    title: "Supplements & Performance History",
    fields: [
      { name: "current_supplements", label: "Current supplements", area: true },
      { name: "previous_supplements", label: "Previous supplements", area: true },
      { name: "ped_history", label: "Any performance-enhancing drugs or compounds currently or previously used", area: true },
      { name: "current_protocols", label: "Current protocols (if applicable)", area: true },
    ],
  },
  {
    title: "Sleep & Recovery",
    fields: [
      { name: "avg_sleep_hours", label: "Average hours of sleep" },
      { name: "sleep_quality", label: "Sleep quality" },
      { name: "stress_level", label: "Stress level" },
      { name: "recovery", label: "Recovery", area: true },
      { name: "daily_activity_steps", label: "Daily activity / steps" },
    ],
  },
  {
    title: "Lifestyle",
    fields: [
      { name: "work_schedule", label: "Work schedule", area: true },
      { name: "daily_activity_level", label: "Daily activity level" },
      { name: "travel_schedule", label: "Travel schedule", area: true },
      { name: "family_social_commitments", label: "Family / social commitments", area: true },
      { name: "biggest_obstacles", label: "Biggest obstacles to consistency", area: true },
    ],
  },
  {
    title: "Previous Coaching Experience",
    fields: [
      { name: "previous_coach", label: "Have you worked with a coach before?", area: true },
      { name: "what_worked", label: "What worked?", area: true },
      { name: "what_didnt_work", label: "What did not work?", area: true },
      { name: "why_stopped", label: "Why did you stop?", area: true },
      { name: "looking_for_now", label: "What are you looking for in a coach now?", area: true },
    ],
  },
  {
    title: "Adherence & Accountability",
    fields: [
      { name: "nutrition_consistency", label: "How consistent are you with nutrition?", area: true },
      { name: "training_consistency", label: "How consistent are you with training?", area: true },
      { name: "biggest_plan_challenges", label: "Biggest challenges with following a plan", area: true },
      { name: "accountability_wanted", label: "How much accountability do you want?" },
      { name: "willingness_to_follow", label: "How willing are you to follow the coach's instructions?" },
    ],
  },
  {
    title: "Mindset",
    fields: [
      { name: "why_change", label: "Why do you want to make this change?", area: true },
      { name: "motivation", label: "What motivates you?", area: true },
      { name: "what_prevented_progress", label: "What has prevented you from reaching your goals?", area: true },
      { name: "commitment_level", label: "How committed are you? (1-10)" },
      { name: "willing_to_change", label: "What are you willing to change?", area: true },
    ],
  },
  {
    title: "Coaching Expectations",
    fields: [
      { name: "expectations", label: "What do you expect from Chris Cormier and the coaching team?", area: true },
      { name: "preferred_communication", label: "Preferred communication style" },
      { name: "support_level", label: "Level of support you're looking for", area: true },
      { name: "success_definition", label: "What would make this coaching experience successful for you?", area: true },
    ],
  },
  {
    title: "Check-In Preferences",
    fields: [
      { name: "checkin_frequency", label: "Preferred check-in frequency" },
      { name: "checkin_method", label: "Preferred communication method (WhatsApp, Email, Video, etc.)" },
      { name: "video_call_availability", label: "Availability for video calls (Elite VIP only)", area: true },
    ],
  },
  {
    title: "Additional Information",
    fields: [
      { name: "additional_notes", label: "Is there anything else you believe your coach should know?", area: true },
    ],
  },
];

const REQUIRED_FIELDS = ["full_name", "email", "phone"];

const buildInitialFields = () => {
  const acc = {};
  for (const s of SECTIONS) {
    for (const f of s.fields) {
      acc[f.name] = "";
    }
  }
  return acc;
};

const renderField = (f, data, set) => {
  const label = (
    <Label className="text-[11px] text-white/60 tracking-[0.15em] uppercase mb-2 block font-semibold">
      {f.label}
    </Label>
  );
  const value = data[f.name] || "";
  if (f.area) {
    return (
      <div key={f.name}>
        {label}
        <Textarea
          data-testid={`fld-${f.name}`}
          rows={3}
          className="premium-input rounded py-2.5 px-3 min-h-[80px]"
          value={value}
          onChange={(e) => set(f.name, e.target.value)}
          placeholder={f.placeholder || ""}
        />
      </div>
    );
  }
  return (
    <div key={f.name}>
      {label}
      <Input
        data-testid={`fld-${f.name}`}
        type={f.type || "text"}
        className="premium-input rounded py-3 px-4"
        value={value}
        onChange={(e) => set(f.name, e.target.value)}
        placeholder={f.placeholder || ""}
      />
    </div>
  );
};

const AssessmentPage = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id") || "";
  const programId = params.get("program_id") || "";

  const [data, setData] = useState({
    ...buildInitialFields(),
    agree_info_accurate: false,
    agree_commit_program: false,
    agree_understand_process: false,
  });
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const totalSteps = SECTIONS.length + 1; // +1 for Client Agreement
  const progress = ((step + 1) / totalSteps) * 100;

  const canSubmit = useMemo(
    () =>
      REQUIRED_FIELDS.every((k) => (data[k] || "").toString().trim().length > 0) &&
      data.agree_info_accurate &&
      data.agree_commit_program &&
      data.agree_understand_process,
    [data]
  );

  const handleSubmit = async () => {
    if (!canSubmit) {
      toast.error("Please complete Name, Email, Phone and all three agreements.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...data,
        stripe_session_id: sessionId || null,
        program_id: programId || null,
      };
      await axios.post(`${API}/coaching/intake`, payload);
      setSubmitted(true);
      toast.success("Assessment submitted");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg =
        typeof detail === "string"
          ? detail
          : Array.isArray(detail)
          ? detail.map((d) => d.msg).join(", ")
          : "Submission failed. Please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const nextStep = () => {
    // Step 0 requires basic personal info
    if (step === 0) {
      const missing = REQUIRED_FIELDS.filter((k) => !(data[k] || "").toString().trim());
      if (missing.length) {
        toast.error(`Please fill: ${missing.map((k) => k.replace("_", " ")).join(", ")}`);
        return;
      }
    }
    setStep((s) => Math.min(s + 1, totalSteps - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const prevStep = () => {
    setStep((s) => Math.max(s - 1, 0));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  /* ------------------------------ SUBMITTED VIEW ------------------------------ */
  if (submitted) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] pt-32 pb-24 px-6" data-testid="assessment-submitted">
        <div className="max-w-2xl mx-auto text-center">
          <motion.div
            initial={{ scale: 0.7, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="inline-flex items-center justify-center w-20 h-20 bg-[#D4AF37] rounded-full mb-8"
          >
            <Check size={38} className="text-black" strokeWidth={3} />
          </motion.div>

          <p className="text-[#D4AF37] text-xs sm:text-sm tracking-[0.35em] uppercase mb-4 font-bold">
            Assessment Received
          </p>
          <h1
            className="font-display text-4xl sm:text-5xl lg:text-6xl text-white mb-6 leading-[0.95] tracking-tight"
            style={{ fontFamily: "'Bebas Neue', sans-serif" }}
          >
            THANK YOU,{" "}
            <span className="text-[#D4AF37]">
              {(data.full_name || "Athlete").split(" ")[0]}
            </span>
          </h1>
          <p className="text-white/75 text-base sm:text-lg leading-relaxed mb-4">
            Your coaching assessment has been submitted. Chris and the team are reviewing your
            information to build your personalized strategy.
          </p>
          <p className="text-white/60 text-sm sm:text-base leading-relaxed mb-10 max-w-xl mx-auto">
            You&rsquo;ll receive next steps within{" "}
            <span className="text-white font-semibold">24-48 hours</span>. In the meantime, connect
            with the team on WhatsApp so we can move you into onboarding.
          </p>
          <a
            href={buildWhatsAppLink(`Hi Chris, I just completed my coaching assessment (${data.full_name}). Ready for the next step.`)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 bg-[#25D366] hover:bg-[#1FB855] text-white px-9 py-4 text-xs sm:text-sm tracking-[0.25em] uppercase font-bold transition-all shadow-[0_15px_45px_-10px_rgba(37,211,102,0.55)]"
            data-testid="submitted-whatsapp-btn"
          >
            <WhatsAppIcon size={20} />
            Message the Coaching Team
            <ArrowRight size={16} />
          </a>
        </div>
      </div>
    );
  }

  /* ------------------------------ FORM VIEW ------------------------------ */
  const isAgreementStep = step === SECTIONS.length;
  const currentSection = SECTIONS[step];

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-24 sm:pt-28 pb-24" data-testid="assessment-page">
      <div className="max-w-3xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-10">
          <div className="inline-flex items-center gap-2 border border-[#D4AF37]/50 bg-black/40 px-4 py-1.5 mb-6">
            <ClipboardList className="w-3 h-3 text-[#D4AF37]" />
            <span className="text-[#D4AF37] text-[10px] sm:text-xs tracking-[0.4em] uppercase font-semibold">
              Coaching Assessment
            </span>
          </div>
          <h1
            className="font-display text-[2.25rem] sm:text-5xl lg:text-6xl text-white leading-[0.95] mb-4 tracking-tight"
            style={{ fontFamily: "'Bebas Neue', sans-serif" }}
          >
            CLIENT INTAKE <br />
            <span className="text-[#D4AF37]">QUESTIONNAIRE</span>
          </h1>
          <p className="text-white/60 text-sm sm:text-base max-w-xl mx-auto leading-relaxed">
            Complete this comprehensive assessment so Chris and the team can build your
            personalized coaching strategy.
          </p>
        </div>

        {/* Progress */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2 text-[10px] sm:text-xs tracking-[0.25em] uppercase text-white/60">
            <span data-testid="assessment-progress-label">
              Step {step + 1} of {totalSteps}
            </span>
            <span className="text-[#D4AF37]">{Math.round(progress)}%</span>
          </div>
          <div className="h-[3px] bg-white/10 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[#D4AF37] to-[#F1D060] transition-all duration-500"
              style={{ width: `${progress}%` }}
              data-testid="assessment-progress-bar"
            />
          </div>
        </div>

        {/* Card */}
        <motion.div
          key={step}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="border border-[#1A1A1A] bg-black/60 backdrop-blur-sm p-6 sm:p-10"
        >
          {!isAgreementStep && (
            <>
              <h2
                className="text-[#D4AF37] text-xl sm:text-2xl uppercase font-bold tracking-wider mb-6"
                style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.08em" }}
              >
                {step + 1}. {currentSection.title}
              </h2>

              <div className="space-y-5">
                {currentSection.fields.map((f) => renderField(f, data, set))}
              </div>
            </>
          )}

          {isAgreementStep && (
            <>
              <h2
                className="text-[#D4AF37] text-xl sm:text-2xl uppercase font-bold tracking-wider mb-6"
                style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.08em" }}
              >
                {totalSteps}. Client Agreement
              </h2>
              <p className="text-white/70 text-sm mb-6 leading-relaxed">
                Please confirm each of the following before submitting your assessment:
              </p>

              <div className="space-y-4">
                <AgreementRow
                  id="agree_info_accurate"
                  checked={data.agree_info_accurate}
                  onCheck={(v) => set("agree_info_accurate", v)}
                  label="I confirm that the information I have provided is accurate."
                />
                <AgreementRow
                  id="agree_commit_program"
                  checked={data.agree_commit_program}
                  onCheck={(v) => set("agree_commit_program", v)}
                  label="I confirm my commitment to following the coaching program."
                />
                <AgreementRow
                  id="agree_understand_process"
                  checked={data.agree_understand_process}
                  onCheck={(v) => set("agree_understand_process", v)}
                  label="I confirm my understanding of the coaching process."
                />
              </div>
            </>
          )}

          {/* Nav */}
          <div className="flex items-center justify-between gap-3 pt-8 mt-8 border-t border-white/5">
            <Button
              type="button"
              variant="ghost"
              onClick={prevStep}
              disabled={step === 0}
              className="text-white/70 hover:text-white hover:bg-white/5 disabled:opacity-30"
              data-testid="btn-prev"
            >
              <ArrowLeft size={14} className="mr-2" />
              Back
            </Button>

            {!isAgreementStep ? (
              <Button
                type="button"
                onClick={nextStep}
                className="bg-[#D4AF37] text-black hover:bg-white px-8 py-3 text-xs tracking-[0.25em] uppercase font-bold rounded-none"
                data-testid="btn-next"
              >
                Next
                <ArrowRight size={14} className="ml-2" />
              </Button>
            ) : (
              <Button
                type="button"
                onClick={handleSubmit}
                disabled={submitting || !canSubmit}
                className="bg-[#D4AF37] text-black hover:bg-white disabled:opacity-50 disabled:hover:bg-[#D4AF37] px-8 py-3 text-xs tracking-[0.25em] uppercase font-bold rounded-none"
                data-testid="btn-submit"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Submitting
                  </>
                ) : (
                  <>
                    Submit Assessment
                    <ArrowRight size={14} className="ml-2" />
                  </>
                )}
              </Button>
            )}
          </div>
        </motion.div>

        {/* Session context (for user reference) */}
        {sessionId && (
          <p className="text-center text-white/30 text-[11px] tracking-wider uppercase mt-6">
            Payment reference: {sessionId.slice(0, 24)}…
          </p>
        )}
      </div>
    </div>
  );
};

const AgreementRow = ({ id, label, checked, onCheck }) => (
  <label
    htmlFor={id}
    className={`flex items-start gap-3 p-4 border cursor-pointer transition-colors ${
      checked ? "border-[#D4AF37]/50 bg-[#D4AF37]/[0.05]" : "border-white/10 bg-black/40 hover:border-white/25"
    }`}
    data-testid={`row-${id}`}
  >
    <Checkbox
      id={id}
      checked={checked}
      onCheckedChange={(v) => onCheck(!!v)}
      className="mt-0.5 border-white/30 data-[state=checked]:bg-[#D4AF37] data-[state=checked]:text-black data-[state=checked]:border-[#D4AF37]"
      data-testid={`cbx-${id}`}
    />
    <span className="text-white/85 text-sm leading-relaxed">{label}</span>
  </label>
);

export default AssessmentPage;
